import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import styles from "./LoginPage.module.css";
import bgImage from "../../assets/images/bg-login.png";
import api from "../../services/api";
import useAuthStore from "../../store/authStore";
import { ENDPOINTS } from "../../utils/endpoints";

const LoginPage = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const loginUser = useAuthStore((state) => state.login);
  const navigate = useNavigate();

  const handleChange = (field) => (e) =>
    setFormData({ ...formData, [field]: e.target.value });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  const handleLogin = async () => {
    setError("");
    setLoading(true);

    try {
      // TODO: Replace with actual FCM device token
      const deviceToken = "your-device-token-placeholder";

      const { data } = await api.post(ENDPOINTS.LOGIN, {
        username: formData.username,
        password: formData.password,
        device_token: deviceToken,
      });

      if (data.success) {
        loginUser(data.accessToken, data.user, data.user_meta);

        // Remember logic
        if (rememberMe) {
          localStorage.setItem("rememberUser", JSON.stringify(formData));
        } else {
          localStorage.removeItem("rememberUser"); // clear if unchecked
        }

        navigate("/dashboard");
      } else {
        setError(data.message || "Login failed");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Hydrate credentials if saved before
  useEffect(() => {
    const savedUser = localStorage.getItem("rememberUser");
    if (savedUser) {
      const parsed = JSON.parse(savedUser);
      setFormData({
        username: parsed.username || "",
        password: parsed.password || "",
      });
      setRememberMe(true);
    }
  }, []);

  return (
    <div className={styles.container}>
      {loading && <div className={styles.loadingOverlay}>Please wait...</div>}

      <div className={styles.left}>
        <img src={bgImage} alt="login-bg" className={styles.bgImage} />
        <div className={styles.logo}>EAU</div>
        <p className={styles.subTitle}>Ecolearn & Alpha Universe Group</p>
      </div>

      <div className={styles.right}>
        <h2 className={styles.title}>USER LOGIN</h2>

        <InputField
          icon={<PersonIcon className={styles.icon} />}
          placeholder="Username"
          value={formData.username}
          onChange={handleChange("username")}
          onKeyPress={handleKeyPress}
        />

        <InputField
          icon={<LockIcon className={styles.icon} />}
          placeholder="Password"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleChange("password")}
          onKeyPress={handleKeyPress}
          showPasswordToggle={true}
          showPassword={showPassword}
          onTogglePassword={togglePasswordVisibility}
        />

        <div className={styles.row}>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            Remember
          </label>
          <span
            className={styles.forgot}
            onClick={() => navigate("/forgotpassword")}
          >
            Forgot Password
          </span>
        </div>

        <button
          className={styles.loginBtn}
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? "Logging in..." : "Login"}
        </button>

        {error && <p className={styles.error}>{error}</p>}

        <p className={styles.signup} onClick={() => navigate("/signup")}>
          Sign Up
        </p>
      </div>
    </div>
  );
};

const InputField = ({
  icon,
  placeholder,
  type = "text",
  value,
  onChange,
  onKeyPress,
  showPasswordToggle = false,
  showPassword = false,
  onTogglePassword,
}) => (
  <div className={styles.inputGroup}>
    {icon && icon}
    <input
      type={type}
      placeholder={placeholder}
      className={styles.input}
      value={value}
      onChange={onChange}
      onKeyPress={onKeyPress}
    />
    {showPasswordToggle && (
      <button
        type="button"
        className={styles.passwordToggle}
        onClick={onTogglePassword}
        tabIndex={-1}
      >
        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
      </button>
    )}
  </div>
);

export default LoginPage;
